/**
 * Phase X.2 — One-to-One Marketing Card.
 * Lets an admin/sales agent push an APPROVED template (portfolio/package/brochure/offer/etc.)
 * from a single conversation. Tracked in marketing_sends so analytics rolls it up.
 *
 * POST /admin/marketing/one-to-one/send
 */
import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, Send, Loader2, Sparkles, FileText, Image as ImageIcon, Film, Package, Award } from "lucide-react";

const CARD_TYPES = [
  { id: "portfolio",  label: "Portfolio",  Icon: ImageIcon },
  { id: "package",    label: "Package",    Icon: Package },
  { id: "brochure",   label: "Brochure",   Icon: FileText },
  { id: "offer",      label: "Offer",      Icon: Award },
  { id: "catalogue",  label: "Catalogue",  Icon: FileText },
  { id: "video",      label: "Video",      Icon: Film },
  { id: "image",      label: "Image",      Icon: ImageIcon },
  { id: "pdf",        label: "PDF",        Icon: FileText },
];

function bodyVarCount(tmpl) {
  const body = (tmpl?.components || []).find(c => (c.type || "").toUpperCase() === "BODY");
  const text = body?.text || "";
  const m = text.match(/{{\s*\d+\s*}}/g) || [];
  return m.length;
}

export default function OneToOneMarketingCard({ conversationId, onClose, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tmpl, setTmpl] = useState(null);
  const [cardType, setCardType] = useState("portfolio");
  const [vars, setVars] = useState([]);
  const [headerMediaUrl, setHeaderMediaUrl] = useState("");
  const [sending, setSending] = useState(false);
  const [assets, setAssets] = useState([]);
  const [showAssets, setShowAssets] = useState(false);

  useEffect(() => {
    api.get("/admin/marketing/templates")
      .then(r => setTemplates((r.data.rows || []).filter(t => (t.status || "").toUpperCase() === "APPROVED")))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!showAssets) return;
    api.get(`/admin/marketing/assets?folder=${cardType}`).catch(() => null)
      .then(r => setAssets(r?.data?.rows || []));
  }, [showAssets, cardType]);

  const variableCount = useMemo(() => bodyVarCount(tmpl), [tmpl]);

  const pickTemplate = (t) => {
    setTmpl(t);
    setVars(new Array(bodyVarCount(t)).fill(""));
  };

  const send = async () => {
    if (!tmpl) { toast.error("Pick an approved template"); return; }
    if (vars.some(v => !v)) { toast.error("Fill all template variables"); return; }
    setSending(true);
    try {
      const r = await api.post("/admin/marketing/one-to-one/send", {
        conversation_id: conversationId,
        template_name: tmpl.name,
        language: tmpl.language || "en_US",
        body_variables: vars,
        header_media_url: headerMediaUrl || null,
        card_type: cardType,
      });
      toast.success(`Sent · wa_id ${r.data.wa_message_id || "—"}`);
      onSent?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="o2o-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-orange-500" />
          <h2 className="font-bold text-[#1a3c5e] flex-1">One-to-One Marketing Card</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-3 border-b bg-gray-50">
          <label className="block text-[10px] uppercase font-bold text-gray-600 mb-1.5">Card type</label>
          <div className="flex flex-wrap gap-1.5">
            {CARD_TYPES.map(c => {
              const I = c.Icon;
              return (
                <button key={c.id} onClick={() => setCardType(c.id)} data-testid={`o2o-card-${c.id}`}
                  className={`px-2.5 py-1 text-[11px] font-bold rounded-full inline-flex items-center gap-1 border ${
                    cardType === c.id
                      ? "bg-orange-100 border-orange-300 text-orange-800"
                      : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}>
                  <I className="w-3 h-3" /> {c.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 grid grid-cols-[280px_1fr] overflow-hidden">
          <div className="border-r border-gray-200 overflow-y-auto">
            {loading && <div className="p-6 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
            {!loading && templates.length === 0 && (
              <div className="p-4 text-xs text-gray-500 italic">
                No APPROVED templates. Go to Marketing → Templates → Sync from Meta.
              </div>
            )}
            {templates.map(t => (
              <button key={`${t.name}-${t.language}`} onClick={() => pickTemplate(t)} data-testid={`o2o-tmpl-${t.name}`}
                className={`w-full text-left px-3 py-2 border-b border-gray-50 hover:bg-gray-50 ${tmpl?.name === t.name ? "bg-orange-50" : ""}`}>
                <div className="font-semibold text-sm text-[#1a3c5e] truncate">{t.name}</div>
                <div className="text-[10px] text-gray-500">{t.language} · {t.category}</div>
              </button>
            ))}
          </div>

          <div className="overflow-y-auto p-4 space-y-3">
            {!tmpl && <div className="text-center text-gray-400 text-sm py-10">Pick an approved template on the left.</div>}
            {tmpl && (
              <>
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs whitespace-pre-wrap" data-testid="o2o-preview">
                  {(tmpl.components || []).find(c => (c.type || "").toUpperCase() === "BODY")?.text || ""}
                </div>
                {variableCount > 0 && (
                  <div className="space-y-2">
                    <div className="text-[10px] uppercase font-bold text-gray-600">Variables</div>
                    {Array.from({ length: variableCount }).map((_, i) => (
                      <input key={i} value={vars[i] || ""} onChange={e => {
                        const c = [...vars]; c[i] = e.target.value; setVars(c);
                      }} placeholder={`{{${i + 1}}}`} data-testid={`o2o-var-${i}`}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs" />
                    ))}
                  </div>
                )}
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] uppercase font-bold text-gray-600">Header media URL (optional)</label>
                    <button onClick={() => setShowAssets(s => !s)} data-testid="o2o-asset-toggle"
                      className="text-[10px] text-blue-600 font-bold">{showAssets ? "Hide" : "Pick from"} library</button>
                  </div>
                  <input value={headerMediaUrl} onChange={e => setHeaderMediaUrl(e.target.value)}
                    placeholder="https://…" data-testid="o2o-header-url"
                    className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs" />
                  {showAssets && (
                    <div className="bg-gray-50 border border-gray-200 rounded p-2 max-h-32 overflow-y-auto space-y-1" data-testid="o2o-asset-list">
                      {assets.length === 0 && <div className="text-[10px] text-gray-400 italic">No assets in this folder.</div>}
                      {assets.map(a => (
                        <button key={a.id} onClick={() => { setHeaderMediaUrl(a.file_url); setShowAssets(false); }}
                          className="w-full text-left text-[11px] hover:bg-white p-1 rounded">
                          <span className="font-bold text-[#1a3c5e]">{a.title}</span>
                          <span className="text-gray-400 ml-1">· {a.kind}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm font-bold border border-gray-200 rounded-lg">Cancel</button>
          <button onClick={send} disabled={sending || !tmpl} data-testid="o2o-send"
            className="px-4 py-1.5 text-sm bg-[#1a3c5e] hover:bg-[#0f2940] text-white font-bold rounded-lg inline-flex items-center gap-1 disabled:opacity-60">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send card
          </button>
        </div>
      </div>
    </div>
  );
}
